import IconProfile from '@hiredigital/ui/Icon/icons/profile.inline.svg';
import IconTalent from '@hiredigital/ui/Icon/icons/talent.inline.svg';
import IconSearch from '@hiredigital/ui/Icon/icons/search.inline.svg';
import IconBrief from '@hiredigital/ui/Icon/icons/brief.inline.svg';
import IconChatUsers from '@hiredigital/ui/Icon/icons/chat-users.inline.svg';
import IconProject from '@hiredigital/ui/Icon/icons/project.inline.svg';
import IconCompany from '@hiredigital/ui/Icon/icons/company.inline.svg';
import IconInvoice from '@hiredigital/ui/Icon/icons/invoice.inline.svg';
import IconTimesheet from '@hiredigital/ui/Icon/icons/timesheet.inline.svg';
import IconEmail from '@hiredigital/ui/Icon/icons/email.inline.svg';
import IconChat from '@hiredigital/ui/Icon/icons/chat.inline.svg';
import IconSkill from '@hiredigital/ui/Icon/icons/skill.inline.svg';

import {
  isTalentAdmin,
  isSearchAdmin,
  isAnyBriefAdmin,
  isClientAdmin,
  isAccountManager,
  isAnyTalentAdmin,
  isAnyAssignmentAdmin,
  isInterviewAdmin,
  isAdmin,
  isFinance,
} from '@helpers/permissions';

export const DASHBOARD_LINK_ICONS = {
  profile: IconProfile,
  talent: IconTalent,
  search: IconSearch,
  brief: IconBrief,
  'chat-users': IconChatUsers,
  project: IconProject,
  company: IconCompany,
  invoice: IconInvoice,
  timesheet: IconTimesheet,
  email: IconEmail,
  chat: IconChat,
  skill: IconSkill,
};

export const SIDEBAR_HARDCODED_ROUTES = new Set([
  '/profiles',
  '/talents',
  '/briefs',
  '/clients',
  '/emails',
]);

export function normalizeDashboardRoute(to) {
  if (!to) return '/';
  return to.startsWith('/') ? to : `/${to}`;
}

export function isHardcodedSidebarRoute(to) {
  const normalized = normalizeDashboardRoute(to);
  const pathname = normalized.split('?')[0];
  return SIDEBAR_HARDCODED_ROUTES.has(pathname);
}

const DASHBOARD_LINKS = {
  '/profiles': {
    to: '/profiles',
    label: 'Profile Review',
    iconKey: 'profile',
    isVisible: (user) => isTalentAdmin(user) || user.adminPermission?.talent,
  },
  '/talents': {
    to: '/talents',
    label: 'Talent Search',
    iconKey: 'search',
    isVisible: (user) => isSearchAdmin(user),
  },
  '/talent-search': {
    to: '/talent-search',
    label: 'Talent Search v2',
    iconKey: 'search',
    isVisible: (user) => isSearchAdmin(user),
  },
  '/resume-search': {
    to: '/resume-search',
    label: 'Resume Search',
    iconKey: 'search',
    isVisible: (user) => isSearchAdmin(user),
  },
  '/portfolio-items': {
    to: '/portfolio-items',
    label: 'Portfolio Items Search',
    iconKey: 'search',
    isVisible: (user) => isSearchAdmin(user) || user.adminPermission?.talent,
  },
  // '/teams': {
  //   to: '/teams',
  //   label: 'Team Profile Review',
  //   iconKey: 'talent',
  //   isVisible: (user) => isTalentAdmin(user) || user.adminPermission?.team,
  // },
  '/briefs': {
    to: '/briefs',
    label: 'Briefs',
    iconKey: 'brief',
    isVisible: (user) => isAnyBriefAdmin(user) || user.adminPermission?.brief,
  },
  '/assignments': {
    to: '/assignments',
    label: 'Assignments',
    iconKey: 'chat-users',
    isVisible: (user) => isAnyAssignmentAdmin(user) || user.adminPermission?.assignment,
  },
  '/projects': {
    to: '/projects',
    label: 'Projects',
    iconKey: 'project',
    isVisible: (user) => isAnyBriefAdmin(user) || user.adminPermission?.project,
  },
  '/jazzhr-jobs': {
    to: '/jazzhr-jobs',
    label: 'JazzHR Jobs',
    iconKey: 'project',
    isVisible: (user) => isAnyBriefAdmin(user) || isSearchAdmin(user),
  },
  '/interview-prompts': {
    to: '/interview-prompts',
    label: 'Interview Prompts',
    iconKey: 'chat',
    isVisible: (user) => isInterviewAdmin(user),
  },
  '/interview-submissions': {
    to: '/interview-submissions',
    label: 'Interview Submissions',
    iconKey: 'chat',
    isVisible: (user) => isInterviewAdmin(user),
  },
  '/clients': {
    to: '/clients',
    label: 'Clients',
    iconKey: 'company',
    isVisible: (user) =>
      isClientAdmin(user) || isAccountManager(user) || user.adminPermission?.org,
  },
  '/org-registrations': {
    to: '/org-registrations',
    label: 'Enterprise Leads',
    iconKey: 'company',
    isVisible: (user) => isAdmin(user) || user.adminPermission?.clientRegistration,
  },
  '/prospects': {
    to: '/prospects',
    label: 'Professional Leads',
    iconKey: 'talent',
    isVisible: (user) => isAdmin(user) || user.adminPermission?.briefRegistration,
  },
  '/prepayments': {
    to: '/prepayments',
    label: 'Prepayments',
    iconKey: 'invoice',
    isVisible: (user) => isAdmin(user) || user.adminPermission?.orgCredit,
  },
  '/finance-talents': {
    to: '/finance-talents',
    label: 'Talents',
    iconKey: 'talent',
    isVisible: (user) => isFinance(user) || user.adminPermission?.finance,
  },
  '/finance-clients': {
    to: '/finance-clients',
    label: 'Clients',
    iconKey: 'company',
    isVisible: (user) => isFinance(user) || user.adminPermission?.finance,
  },
  '/wise-recipients': {
    to: '/wise-recipients',
    label: 'Wise Recipients',
    iconKey: 'company',
    isVisible: (user) => isFinance(user) || user.adminPermission?.finance,
  },
  // '/timesheets': {
  //   to: '/timesheets',
  //   label: 'Timesheet Search (old)',
  //   iconKey: 'timesheet',
  //   isVisible: (user) =>
  //     isAdmin(user) ||
  //     isFinance(user) ||
  //     user.adminPermission?.timesheet ||
  //     user.adminPermission?.timetracker,
  // },
  '/events': {
    to: '/events',
    label: 'Timetracker Summary',
    iconKey: 'search',
    isVisible: (user) =>
      isAdmin(user) ||
      isFinance(user) ||
      user.adminPermission?.timesheet ||
      user.adminPermission?.timetracker,
  },
  '/timetracker/events': {
    to: '/timetracker/events',
    label: 'Timetracker Events',
    iconKey: 'search',
    isVisible: (user) =>
      isAdmin(user) ||
      isFinance(user) ||
      user.adminPermission?.timesheet ||
      user.adminPermission?.timetracker,
  },
  '/timetracker/tree': {
    to: '/timetracker/tree',
    label: 'Timetracker Tree by Talent',
    iconKey: 'timesheet',
    isVisible: (user) =>
      isAdmin(user) ||
      isFinance(user) ||
      user.adminPermission?.timesheet ||
      user.adminPermission?.timetracker,
  },
  '/timetracker/tree?topLevelOrganization=true': {
    to: '/timetracker/tree?topLevelOrganization=true',
    label: 'Timetracker Tree by Client',
    iconKey: 'timesheet',
    isVisible: (user) =>
      isAdmin(user) ||
      isFinance(user) ||
      user.adminPermission?.timesheet ||
      user.adminPermission?.timetracker,
  },
  '/emails': {
    to: '/emails',
    label: 'Email Search',
    iconKey: 'email',
    isVisible: (user) => isClientAdmin(user) || user.adminPermission?.email,
  },
  '/email-templates': {
    to: '/email-templates',
    label: 'Email Templates',
    iconKey: 'email',
    isVisible: (user) => isAdmin(user),
  },
  // '/brief-registrations': {
  //   to: '/brief-registrations',
  //   label: 'Brief Registrations',
  //   iconKey: 'brief',
  //   isVisible: (user) => isAdmin(user) || user.adminPermission?.briefRegistration,
  // },
  '/talent-registrations': {
    to: '/talent-registrations',
    label: 'Talent Registrations',
    iconKey: 'profile',
    isVisible: (user) => isAnyTalentAdmin(user) || user.adminPermission?.userRegistration,
  },
  '/notifications': {
    to: '/notifications',
    label: 'Notifications',
    iconKey: 'talent',
    isVisible: (user) => isAdmin(user),
  },
  '/messages': {
    to: '/messages',
    label: 'Message Review',
    iconKey: 'chat',
    isVisible: (user) => isAdmin(user) || user.adminPermission?.discussion,
  },
  '/support': {
    to: '/support',
    label: 'Support User Management',
    iconKey: 'talent',
    isVisible: (user) => isAdmin(user),
  },
  '/skills': {
    to: '/skills',
    label: 'Skills',
    iconKey: 'skill',
    isVisible: (user) => isAdmin(user),
  },
  // '/bills': {
  //   to: '/bills',
  //   label: 'Bills',
  //   iconKey: 'invoice',
  //   isVisible: (user) => isAdmin(user),
  // },
};

export function getDashboardLink(to) {
  return DASHBOARD_LINKS[normalizeDashboardRoute(to)];
}

export function isDashboardLinkVisible(user, to) {
  const link = getDashboardLink(to);
  if (!link) return false;
  return link.isVisible(user);
}

export function getVisiblePinnedLinks(user, pinnedRoutes) {
  return pinnedRoutes
    .map((route) => getDashboardLink(route))
    .filter((link) => link && link.isVisible(user));
}
